import {_serializeSingle, _serializeExcept, _serializeList, serializeHistoryChangeDetail} from '../core';
import type {ServerData, ServerRecord} from '../core';
import type {DomainOf} from '../../../../types/domain';
import type {WithLabels, WithDepartment, WithChanges} from '../_extras';
import {serializeLabelList, serializeMemberBff, serializeDepartment} from '../member';

/** 스키마 밖에서 조립되는 관계 필드 — 서버 응답 계약의 일부. */
type DocumentDraftBffExtras = WithLabels & WithDepartment & {
    member?: unknown;
};
type DraftHistoryExtras = WithChanges;

export function serializeDocumentDraft(data: ServerData) {
    return _serializeSingle("documentDraft", data);
}

/** expansion 응답이 싣지 않는 documentDraft 필드 */
const EXPANSION_DOCUMENT_DRAFT_OMIT = ["externalId", "memberId"] as const;

export function serializeDocumentDraftExpansion(data: ServerData) {
    return _serializeExcept("documentDraft", data, EXPANSION_DOCUMENT_DRAFT_OMIT);
}

export function serializeDocumentDraftList(data: ServerData) {
    return _serializeList("documentDraft", data);
}

export function serializeDocumentDraftBff(data: ServerData) {
    if (!data) return null;
    const draft: DomainOf<"documentDraft"> & DocumentDraftBffExtras = _serializeSingle("documentDraft", data);
    if (data.labels) draft.labels = serializeLabelList(data.labels);
    // 작성자
    if (data.member) draft.member = serializeMemberBff(data.member);
    // 귀속 부서 (서버: department = 단일 DepartmentInternalWebResponse, 작성 시점 현재 부서)
    if (data.department) draft.department = serializeDepartment(data.department);
    return draft;
}

/** 목록·검색 응답이 싣지 않는 필드 — 본문과 임시저장 본문은 상세에만 */
const DRAFT_SUMMARY_OMIT = ["form", "tempSaveForm"] as const;

export function serializeDocumentDraftBffSummary(data: ServerData) {
    if (!data) return null;
    const draft: DomainOf<"documentDraft"> & DocumentDraftBffExtras =
        _serializeExcept("documentDraft", data, DRAFT_SUMMARY_OMIT) as
            DomainOf<"documentDraft"> & DocumentDraftBffExtras;
    if (data.labels) draft.labels = serializeLabelList(data.labels);
    if (data.member) draft.member = serializeMemberBff(data.member);
    if (data.department) draft.department = serializeDepartment(data.department);
    return draft;
}

export function serializeDocumentDraftBffSummaryList(data: ServerData) {
    if (!data || !Array.isArray(data)) return [];
    return data.map((item: ServerRecord) => serializeDocumentDraftBffSummary(item));
}

/**
 * admin 응답이 싣지 않는 documentDraft 필드 — 본문과 임시저장 본문.
 * admin 역할엔 초안 상세 endpoint 자체가 없어 form 을 주는 경로가 하나도 없다
 * (member·dept-head 는 상세가 있어 목록에만 없는 입도차다).
 * admin 화면이 이 필드들을 읽는 곳도 0곳이다.
 */
const ADMIN_DOCUMENT_DRAFT_OMIT = ["form", "tempSaveForm"] as const;

export function serializeDocumentDraftAdminBff(data: ServerData) {
    if (!data) return null;
    const draft = _serializeExcept("documentDraft", data, ADMIN_DOCUMENT_DRAFT_OMIT) as
        DomainOf<"documentDraft"> & DocumentDraftBffExtras;
    if (data.labels) draft.labels = serializeLabelList(data.labels);
    // 작성자
    if (data.member) draft.member = serializeMemberBff(data.member);
    // 귀속 부서 (서버: department = 단일 DepartmentInternalWebResponse, 작성 시점 현재 부서)
    if (data.department) draft.department = serializeDepartment(data.department);
    return draft;
}

export function serializeDocumentDraftAdminBffList(data: ServerData) {
    if (!data || !Array.isArray(data)) return [];
    return data.map((item: ServerRecord) => serializeDocumentDraftAdminBff(item));
}

export function serializeDocumentDraftHistory(data: ServerData) {
    if (!data) return null;
    const history: DomainOf<"documentDraftHistory"> & DraftHistoryExtras = _serializeSingle("documentDraftHistory", data);
    if (Array.isArray(data.changes)) {
        const serializedChanges = data.changes.map((item: ServerRecord) => ({
            ...serializeHistoryChangeDetail(item),
            ..._serializeSingle("documentDraftHistoryDetail", item),
        }));
        history.changes = serializedChanges;
    }
    return history;
}

export function serializeDocumentDraftHistoryList(data: ServerData) {
    if (!data || !Array.isArray(data)) return [];
    return data.map((item: ServerRecord) => serializeDocumentDraftHistory(item));
}
